import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Select, DatePicker, 
  Card, Row, Col, Typography, Spin, Tag, notification, Space 
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, 
  FilePdfOutlined, DownloadOutlined 
} from '@ant-design/icons';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const API_URL = {
  sales: 'https://agpipefitting.org.et/api/sales',
  products: 'https://agpipefitting.org.et/api/products'
};

const SalesManagement = () => {
  const [sales, setSales] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [selectedSale, setSelectedSale] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [reportType, setReportType] = useState('weekly');
  const [productType, setProductType] = useState('all');
  const [dateRange, setDateRange] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const grouped = sales.reduce((acc, sale) => {
      const key = sale.customer_name;
      if (!acc[key]) {
        acc[key] = {
          customer_name: sale.customer_name,
          total: 0,
          sales: []
        };
      }
      const total = Number(sale.total_price) || 0;
      acc[key].sales.push(sale);
      acc[key].total += total;
      return acc;
    }, {});
    
    setCustomerGroups(Object.values(grouped));
  }, [sales]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [salesRes, productsRes] = await Promise.all([
        axios.get(API_URL.sales),
        axios.get(API_URL.products)
      ]);
      
      setSales(salesRes.data.map(sale => ({
        ...sale,
        sale_date: moment(sale.sale_date).format('YYYY-MM-DD'),
        total_price: Number(sale.total_price) || 0
      })));
      
      setProducts(productsRes.data);
      setLoading(false);
    } catch (error) {
      notification.error({ message: 'Error fetching data', description: error.message });
      setLoading(false);
    }
  };

  // Function to check if referenceNo already exists
  const isReferenceNoDuplicate = (referenceNo, currentSaleId = null) => {
    return sales.some(sale => {
      // If editing, exclude the current sale from duplicate check
      if (currentSaleId && sale.id === currentSaleId) {
        return false;
      }
      return sale.referenceNo === referenceNo;
    });
  };

  const handleSubmit = async (values) => { 
    setSubmitting(true);
    try {
      // Check for duplicate referenceNo
      const currentSaleId = selectedSale ? selectedSale.id : null;
      if (isReferenceNoDuplicate(values.referenceNo, currentSaleId)) {
        notification.error({
          message: 'Duplicate Reference Number',
          description: 'This reference number already exists. Please use a different one.'
        });
        setSubmitting(false);
        return;
      }

      const salesData = values.items.map(item => {
        const product = products.find(p => p.id === item.product_id);
        const quantity = Number(item.quantity) || 0;
        const price = product ? Number(product.price) || 0 : 0;
        
        return {
          customer_name: values.customer_name,
          referenceNo: values.referenceNo,
          salesType: values.salesType,
          sale_date: values.sale_date.format('YYYY-MM-DD'),
          product_id: item.product_id,
          quantity: quantity,
          unitPrice: price,
          total_price: quantity * price
        };
      });

      if (selectedSale) {
        await axios.delete(`${API_URL.sales}/${selectedSale.id}`);
        await axios.post(API_URL.sales, { sales: salesData });
        notification.success({ message: 'Sale updated successfully!' });
      } else {
        await axios.post(API_URL.sales, { sales: salesData });
        notification.success({ message: 'Sales created successfully!' });
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchData();
    } catch (error) {
      notification.error({ 
        message: 'Operation Failed', 
        description: error.response?.data?.message || error.message 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: 'Are you sure you want to delete this sale?',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`${API_URL.sales}/${id}`);
          notification.success({ message: 'Sale Deleted Successfully' });
          fetchData();
        } catch (error) {
          notification.error({ message: 'Deletion Failed', description: error.message });
        }
      }
    });
  };

  const downloadPDF = (sale) => {
    const doc = new jsPDF();
    const invoiceNumber = `INV-${sale.id.toString().padStart(5, '0')}`;
    const product = products.find(p => p.id === sale.product_id);
    const total = Number(sale.total_price) || 0;

    doc.setFillColor(44, 62, 80);
    doc.rect(0, 0, 210, 40, 'F');
    doc.setFontSize(28);
    doc.setTextColor(255, 255, 255);
    doc.text("AG Pipe Fittings", 14, 22);
    
    doc.setFontSize(12);
    doc.text(`Invoice #: ${invoiceNumber}`, 14, 32);
    doc.text(`Ref No: ${sale.referenceNo || 'N/A'}`, 14, 38);
    doc.text(`Date: ${moment(sale.sale_date).format('YYYY-MM-DD')}`, 14, 44);
    doc.text(`Sales Type: ${sale.salesType || 'N/A'}`, 14, 50);
    
    doc.setTextColor(0, 0, 0);
    doc.text(`Bill To: ${sale.customer_name}`, 14, 60);

    doc.autoTable({
      startY: 70,
      head: [['Product', 'Description', 'Unit Price', 'Quantity', 'Total']],
      body: [[
        product?.type || 'N/A',
        `ETB ${(Number(product?.price) || 0).toFixed(2)}`,
        sale.quantity,
        `ETB ${total.toFixed(2)}`
      ]],
      theme: 'grid',
      styles: { fontSize: 10 },
      headStyles: { 
        fillColor: [44, 62, 80],
        textColor: 255,
        fontStyle: 'bold'
      }
    });

    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(12);
    doc.text(`Total: ETB ${total.toFixed(2)}`, 14, finalY);

    doc.save(`${invoiceNumber}.pdf`);
  };

  const downloadReportPDF = () => {
    const doc = new jsPDF();
    const title = 'Sales Report by Type';
    
    doc.setFontSize(18);
    doc.text(title, 14, 22);
    
    doc.setFontSize(12);
    doc.text(`Report Period: ${reportType}`, 14, 32);
    doc.text(`Generated: ${moment().format('YYYY-MM-DD HH:mm')}`, 14, 38);

    const columns = [
      { header: 'Period', dataKey: 'period' },
      { header: 'Cash Sales (ETB)', dataKey: 'Cash' },
      { header: 'Credit Sales (ETB)', dataKey: 'Credit' },
      { header: 'Gift Sales (ETB)', dataKey: 'Gift' },
      { header: 'Total Sales (ETB)', dataKey: 'total' }
    ];

    const data = reportData.map(item => ({
      period: item.period,
      Cash: item.Cash?.toFixed(2) || '0.00',
      Credit: item.Credit?.toFixed(2) || '0.00',
      Gift: item.Gift?.toFixed(2) || '0.00',
      total: item.total?.toFixed(2) || '0.00'
    }));

    doc.autoTable({
      startY: 45,
      columns,
      body: data,
      theme: 'grid',
      styles: { fontSize: 10 },
      headStyles: { 
        fillColor: [44, 62, 80],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        period: { cellWidth: 40 },
        Cash: { cellWidth: 30 },
        Credit: { cellWidth: 30 },
        Gift: { cellWidth: 30 },
        total: { cellWidth: 30 }
      }
    });

    doc.save(`sales-report-${moment().format('YYYYMMDDHHmm')}.pdf`);
  };

  const generateReport = async () => {
    setReportLoading(true);
    try {
      const periods = {};
      
      sales.forEach(sale => {
        const period = moment(sale.sale_date).format(
          reportType === 'weekly' ? 'YYYY-[W]WW' :
          reportType === 'monthly' ? 'YYYY-MM' :
          reportType === 'yearly' ? 'YYYY' : 'YYYY-MM-DD'
        );

        if (!periods[period]) {
          periods[period] = {
            period,
            Cash: 0,
            Credit: 0,
            Gift: 0,
            total: 0
          };
        }

        const matchesType = productType === 'all' || 
          products.find(p => p.id === sale.product_id)?.type === productType;
        
        const matchesDate = reportType !== 'custom' || 
          (dateRange[0] && dateRange[1] && 
          moment(sale.sale_date).isBetween(dateRange[0], dateRange[1], 'day', '[]'));

        if (matchesType && matchesDate) {
          const amount = Number(sale.total_price) || 0;
          periods[period][sale.salesType] += amount;
          periods[period].total += amount;
        }
      });

      const sortedReport = Object.values(periods)
        .sort((a, b) => a.period.localeCompare(b.period))
        .map(item => ({
          ...item,
          Cash: Number(item.Cash),
          Credit: Number(item.Credit),
          Gift: Number(item.Gift),
          total: Number(item.total)
        }));
        
      setReportData(sortedReport);
    } catch (error) {
      notification.error({ message: 'Report generation failed', description: error.message });
    } finally {
      setReportLoading(false);
    }
  };

  const expandedRowRender = (record) => {
    const columns = [
      {
        title: 'Date',
        dataIndex: 'sale_date',
        render: date => moment(date).format('YYYY-MM-DD')
      },
      {
        title: 'Reference No',
        dataIndex: 'referenceNo'
      },
      {
        title: 'Product',
        render: (_, sale) => {
          const product = products.find(p => p.id === sale.product_id);
          return (
            <div>
              <Tag color="blue">{product?.type || 'N/A'} ({product?.size || 'N/A'})</Tag>
            </div>
          );
        }
      },
      {
        title: 'Sales Type',
        dataIndex: 'salesType',
        render: (type) => {
          let color = type === 'Cash' ? 'green' : type === 'Credit' ? 'orange' : 'blue';
          return <Tag color={color}>{type}</Tag>;
        }
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity'
      },
      {
        title: 'Total',
        render: (_, sale) => `ETB ${(Number(sale.total_price) || 0).toFixed(2)}`
      },
      {
        title: 'Actions',
        render: (_, sale) => (
          <Space>
            <Button 
              icon={<FilePdfOutlined />} 
              onClick={() => downloadPDF(sale)}
            />
            <Button 
              icon={<EditOutlined />}
              onClick={() => handleEdit(sale)}
            />
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => handleDelete(sale.id)}
            />
          </Space>
        )
      }
    ];

    return (
      <Table
        columns={columns}
        dataSource={record.sales}
        pagination={false}
        rowKey="id"
        bordered
      />
    );
  };

  const handleEdit = (sale) => {
    const items = sale.sales.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      unitPrice: item.unitPrice
    }));

    setSelectedSale({
      id: sale.sales[0].id,
      customer_name: sale.customer_name,
      referenceNo: sale.sales[0].referenceNo,
      salesType: sale.sales[0].salesType,
      sale_date: moment(sale.sales[0].sale_date),
      items
    });

    form.setFieldsValue({
      customer_name: sale.customer_name,
      referenceNo: sale.sales[0].referenceNo,
      salesType: sale.sales[0].salesType,
      sale_date: moment(sale.sales[0].sale_date),
      items
    });
    setModalVisible(true);
  };

  const columns = [
    {
      title: 'Customer',
      dataIndex: 'customer_name',
      render: text => <Text strong>{text}</Text>
    },
    {
      title: 'Total Sales',
      render: (_, record) => `ETB ${(Number(record.total) || 0).toFixed(2)}`,
      sorter: (a, b) => (Number(a.total) || 0) - (Number(b.total) || 0)
    },
    {
      title: 'Transactions',
      render: (_, record) => `${record.sales.length} sales`
    },
  ];

  return (
    <div className="p-6">
      <Card className="shadow-lg mb-6">
        <Title level={4} className="mb-4">Sales Analytics</Title>
        <Row gutter={16}>
          <Col span={6}>
            <Select
              className="w-full"
              value={reportType}
              onChange={setReportType}
              options={[
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'yearly', label: 'Yearly' },
                { value: 'custom', label: 'Custom Range' },
              ]}
            />
          </Col>
          <Col span={6}>
            <Select
              className="w-full"
              value={productType}
              onChange={setProductType}
              options={[
                { value: 'all', label: 'All Products' },
                ...Array.from(new Set(products.map(p => p.type)))
                  .map(type => ({ value: type, label: type }))
              ]}
            />
          </Col>
          <Col span={8}>
            {reportType === 'custom' && (
              <RangePicker
                className="w-full"
                onChange={dates => setDateRange(dates)}
              />
            )}
          </Col>
          <Col span={4}>
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={generateReport}
                loading={reportLoading}
              >
                Generate
              </Button>
              <Button
                type="default"
                icon={<FilePdfOutlined />}
                onClick={downloadReportPDF}
                disabled={reportData.length === 0}
              >
                PDF
              </Button>
            </Space>
          </Col>
        </Row>

        {reportData.length > 0 && (
          <div className="mt-6">
            <Title level={5} className="mb-4">Sales Report</Title>
            <BarChart
              width={800}
              height={300}
              data={reportData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Cash" fill="#8884d8" />
              <Bar dataKey="Credit" fill="#82ca9d" />
              <Bar dataKey="Gift" fill="#ffc658" />
            </BarChart>

            <Table
              dataSource={reportData}
              pagination={false}
              className="mt-4"
              rowKey="period"
              columns={[
                { 
                  title: 'Period', 
                  dataIndex: 'period',
                  sorter: (a, b) => a.period.localeCompare(b.period)
                },
                { 
                  title: 'Cash Sales',
                  dataIndex: 'Cash',
                  render: (value) => `ETB ${value?.toFixed(2) || '0.00'}`,
                  sorter: (a, b) => a.Cash - b.Cash
                },
                { 
                  title: 'Credit Sales',
                  dataIndex: 'Credit',
                  render: (value) => `ETB ${value?.toFixed(2) || '0.00'}`,
                  sorter: (a, b) => a.Credit - b.Credit
                },
                { 
                  title: 'Gift Sales',
                  dataIndex: 'Gift',
                  render: (value) => `ETB ${value?.toFixed(2) || '0.00'}`,
                  sorter: (a, b) => a.Gift - b.Gift
                },
                { 
                  title: 'Total Sales', 
                  dataIndex: 'total',
                  render: (value) => `ETB ${value?.toFixed(2) || '0.00'}`,
                  sorter: (a, b) => a.total - b.total
                }
              ]}
            />
          </div>
        )}
      </Card>

      <Card className="shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Title level={3} className="mb-1">Customer Sales</Title>
            <Text type="secondary">Expand rows to view transaction details</Text>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedSale(null);
              form.resetFields();
              setModalVisible(true);
            }}
          >
            New Sale
          </Button>
        </div>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={customerGroups}
            rowKey="customer_name"
            expandable={{ expandedRowRender }}
            pagination={{ pageSize: 8 }}
            bordered
            scroll={{ x: true }}
          />
        </Spin>
      </Card>

      <Modal
        title={selectedSale ? 'Edit Sale' : 'New Sale'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        destroyOnClose
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ items: [{}] }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Customer Name"
                name="customer_name"
                rules={[{ required: true, message: 'Please enter customer name' }]}
              >
                <Input placeholder="Enter customer name" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Reference No"
                name="referenceNo"
                rules={[
                  { required: true, message: 'Please enter reference number' },
                  {
                    validator: async (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }
                      
                      const currentSaleId = selectedSale ? selectedSale.id : null;
                      if (isReferenceNoDuplicate(value, currentSaleId)) {
                        return Promise.reject('This reference number already exists. Please use a different one.');
                      }
                      
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input placeholder="Enter reference number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Sale Date"
                name="sale_date"
                rules={[{ required: true, message: 'Please select date' }]}
              >
                <DatePicker className="w-full" format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Sales Type"
                name="salesType"
                rules={[{ required: true, message: 'Please select sales type' }]}
              >
                <Select placeholder="Select sales type">
                  <Option value="Cash">Cash</Option>
                  <Option value="Credit">Credit</Option>
                  <Option value="Gift">Gift</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row key={key} gutter={16} align="middle">
                    <Col span={10}>
                      <Form.Item
                        {...restField}
                        name={[name, 'product_id']}
                        rules={[{ required: true, message: 'Please select product' }]}
                      >
                        <Select
                          showSearch
                          placeholder="Select product"
                          optionFilterProp="children"
                          onChange={(value) => {
                            const product = products.find(p => p.id === value);
                            form.setFieldsValue({
                              items: form.getFieldValue('items').map((item, index) => 
                                index === name ? { 
                                  ...item, 
                                  unitPrice: Number(product?.price) || 0 
                                } : item
                              )
                            });
                          }}
                        >
                          {products.map(product => (
                            <Option key={product.id} value={product.id}>
                              ({product.type} - {product.size})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'quantity']}
                        rules={[{ 
                          required: true, 
                          message: 'Quantity required',
                          type: 'number',
                          min: 1,
                          transform: value => Number(value)
                        }]}
                      >
                        <Input type="number" min={1} placeholder="Qty" />
                      </Form.Item>
                    </Col>

                    <Col span={4}>
                      <Form.Item
                        {...restField}
                        name={[name, 'unitPrice']}
                      >
                        <Input 
                          disabled 
                          placeholder="Price"
                          value={Number(form.getFieldValue(['items', name, 'unitPrice']) || 0).toFixed(2)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={4}>
                      <Form.Item>
                        <Input 
                          disabled 
                          placeholder="Total"
                          value={(
                            (Number(form.getFieldValue(['items', name, 'quantity']) || 0) *
                            Number(form.getFieldValue(['items', name, 'unitPrice']) || 0))
                          ).toFixed(2)}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={2}>
                      {fields.length > 1 && (
                        <Button
                          danger
                          onClick={() => remove(name)}
                          icon={<DeleteOutlined />}
                        />
                      )}
                    </Col>
                  </Row>
                ))}

                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Add Product
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <div className="flex justify-end gap-3 mt-6">
            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {selectedSale ? 'Update Sales' : 'Create Sales'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default SalesManagement;